// @mui material components
import { Snackbar, Alert, Grid, Card, Icon, CircularProgress, Backdrop } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import MDBadge from "theme/components/MDBadge";
import usePageTitle from "app/pageTitle";

function UserSwaps() {
  const pageTitle = "Swap History";
  usePageTitle(pageTitle);

  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [swaps, setSwaps] = useState([]);

  function formatAmount(amt, symbol) {
    if (amt > 1) {
      return `${symbol}${amt.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `${symbol}${amt}`;
  }

  function getStatusColor(stat) {
    let statusColor = "warning";
    if (stat.toUpperCase() === "COMPLETED") {
      statusColor = "success";
    } else if (stat.toUpperCase() === "SELLER-DECLINED") {
      statusColor = "dark";
    } else if (stat.toUpperCase() === "ERROR") {
      statusColor = "error";
    } else if (stat.toUpperCase() === "FAILED") {
      statusColor = "error";
    } else if (stat.toUpperCase() === "AWAITING-SELLER-PAYMENT") {
      statusColor = "info";
    } else {
      statusColor = "warning";
    }

    return statusColor;
  }

  const swapColumns = [
    { Header: "Wallet", accessor: "wallet", align: "left" },
    { Header: "Side", accessor: "side", align: "center" },
    { Header: "Currency", accessor: "digitalCurrency", align: "center" },
    { Header: "Fiat", accessor: "fiatCurrency", align: "center" },
    { Header: "Amount", accessor: "amount", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Created", accessor: "created", align: "center" },
    { Header: "Details", accessor: "more", align: "center" },
  ];

  const buildSwapRows = (data) => {
    const swapRows = [];
    data.map((swap) => {
      // console.log(swap);
      swapRows.push({
        wallet: <b>{swap.digital_wallet.title}</b>,
        digitalCurrency: swap.wallet_currency.currency_code,
        fiatCurrency: swap.balance_currency.currency_code,
        side: (
          <MDBadge
            badgeContent={swap.side}
            variant="contained"
            container
            color={swap.side === "SELL" ? "error" : "success"}
          />
        ),
        amount: formatAmount(
          swap.amount / swap.wallet_currency.multiplier,
          swap.wallet_currency.symbol
        ),
        status: (
          <MDBadge
            badgeContent={swap.status}
            variant="contained"
            container
            color={getStatusColor(swap.status)}
          />
        ),
        created: swap.created,
        more: (
          <Link to={`/p2p/ewallets/history/${swap.reference}`}>
            <MDButton variant="outlined" color="info" iconOnly circular>
              <Icon sx={{ fontWeight: "bold" }}>keyboard_double_arrow_right</Icon>
            </MDButton>
          </Link>
        ),
      });
      return null;
    });

    return swapRows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("ewallets/user/swaps.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            const rows = buildSwapRows(result.data.data.swaps);
            setSwaps(rows);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred. Please try again later.");
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <MDBox sx={{ marginBottom: "40px" }}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between">
                    <MDBox>
                      <MDButton
                        variant="link"
                        color="dark"
                        onClick={() => {
                          navigate("/p2p/ewallets");
                        }}
                      >
                        MarketPlace
                      </MDButton>
                    </MDBox>
                    <MDBox>
                      <MDButton
                        variant="link"
                        color="dark"
                        onClick={() => {
                          navigate("/p2p/ewallets/create");
                        }}
                      >
                        <Icon>add</Icon>&nbsp; Post Request
                      </MDButton>
                      <MDButton
                        variant="link"
                        color="dark"
                        onClick={() => {
                          navigate("/p2p/ewallets/my-requests");
                        }}
                      >
                        My Requests
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <MDBox>
                  <Card sx={{ height: "100%", marginTop: "0px" }}>
                    <MDBox p={3}>
                      <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                        Swap History
                      </MDTypography>
                      <MDBox mt={5}>
                        <DataTable
                          table={{ columns: swapColumns, rows: swaps }}
                          isSorted={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                        />
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UserSwaps;
